import type { ReactNode } from "react";
import { Loader2 } from "lucide-react";
import type { UseFormReturn } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { AddressAutocomplete } from "@/components/ui/address-autocomplete";
import { buildingRegistryFromAddress, formatAdsAddress } from "@/hooks/useAddressSearch";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { BuildingPlanChoice } from "@/domains/billing/components/BuildingPlanChoice";
import { BuildingPlanRestriction } from "@/domains/billing/components/BuildingPlanRestriction";
import type { BillingOverview, BillingPlan, BuildingPlanTier } from "@/domains/billing/types";
import { stripBuildingMarkup, type BuildingCreationValues } from "./buildingCreationSchema";

interface PlanSelection {
  plans?: BillingPlan[];
  tier: BuildingPlanTier;
  setTier: (tier: BuildingPlanTier) => void;
  quote?: BillingOverview["add_building_quote"];
  currency?: string;
  unavailable: boolean;
  isLoading?: boolean;
  reason?: string;
  needsBillingSetup?: boolean;
  onSetup?: () => void;
  retry: () => void;
}

function CreationFields({
  form,
  disabled,
}: {
  form: UseFormReturn<BuildingCreationValues>;
  disabled: boolean;
}) {
  const { t } = useLanguage();
  return (
    <>
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem className="space-y-2">
            <FormLabel className="text-xs uppercase tracking-wide">
              {t.buildings.form.name}
            </FormLabel>
            <FormControl>
              <Input {...field} placeholder={t.buildings.form.name} required disabled={disabled} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="address"
        render={({ field }) => (
          <FormItem className="space-y-2">
            <FormLabel className="text-xs uppercase tracking-wide">
              {t.buildings.form.address}
            </FormLabel>
            <FormControl>
              <AddressAutocomplete
                {...field}
                sanitizeInput={stripBuildingMarkup}
                className="rounded-md"
                placeholder={t.buildings.form.address}
                required
                disabled={disabled}
                onSelect={(result) => {
                  field.onChange(stripBuildingMarkup(formatAdsAddress(result)));
                  const registry = buildingRegistryFromAddress(result);
                  if (registry)
                    form.setValue("registry_code", registry, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="registry_code"
        render={({ field }) => (
          <FormItem className="space-y-2">
            <FormLabel className="text-xs uppercase tracking-wide">
              {t.buildings.registryCode}
            </FormLabel>
            <FormControl>
              <Input
                {...field}
                placeholder={t.buildings.registryCode}
                inputMode="numeric"
                maxLength={9}
                pattern="[0-9]{9}"
                disabled={disabled}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
}

function CreationPlan({ selection, disabled }: { selection: PlanSelection; disabled: boolean }) {
  const { t } = useLanguage();
  const presentation =
    selection.quote && selection.currency
      ? { kind: "add" as const, quote: selection.quote, currency: selection.currency }
      : undefined;
  return (
    <>
      {selection.plans && presentation && (
        <BuildingPlanChoice
          plans={selection.plans}
          tier={selection.tier}
          onChange={selection.setTier}
          disabled={disabled}
          presentation={presentation}
        />
      )}
      {selection.isLoading && (
        <p role="status" className="text-sm text-muted-foreground">
          {t.billing.consent.loading}
        </p>
      )}
      {selection.unavailable && (
        <div className="space-y-2">
          <p role="status" className="text-sm text-destructive">
            {t.billing.consent.quoteUnavailable}
          </p>
          <Button type="button" variant="outline" onClick={selection.retry} disabled={disabled}>
            {t.billing.consent.retry}
          </Button>
        </div>
      )}
      <BuildingPlanRestriction
        reason={selection.reason}
        needsBillingSetup={selection.needsBillingSetup}
        onSetup={selection.onSetup}
      />
    </>
  );
}

interface BuildingCreationDialogContentProps {
  form: UseFormReturn<BuildingCreationValues>;
  onSubmit: (values: BuildingCreationValues) => Promise<void>;
  onCancel: () => void;
  disabled: boolean;
  submitting: boolean;
  submitDisabled: boolean;
  selection?: PlanSelection;
  reason?: string;
}

export function BuildingCreationDialogShell({ children }: { children: ReactNode }) {
  const { t } = useLanguage();
  return (
    <DialogContent
      className="max-h-[calc(100dvh-2rem)] max-w-md overflow-y-auto"
      aria-describedby={undefined}
    >
      <DialogHeader className="pb-1">
        <DialogTitle>{t.billing.addDialog.title}</DialogTitle>
      </DialogHeader>
      {children}
    </DialogContent>
  );
}

export function BuildingCreationDialogContent(props: BuildingCreationDialogContentProps) {
  const { form, onSubmit, onCancel, disabled, submitting, submitDisabled, selection, reason } =
    props;
  const { t } = useLanguage();
  return (
    <BuildingCreationDialogShell>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="space-y-5">
          <CreationFields form={form} disabled={disabled} />
          {selection && <CreationPlan selection={selection} disabled={disabled} />}
          {reason && (
            <p role="status" className="text-sm text-destructive">
              {reason}
            </p>
          )}
          <DialogFooter className="pt-1">
            <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
              {t.billing.addDialog.cancel}
            </Button>
            <Button type="submit" disabled={submitDisabled}>
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {selection ? t.billing.consent.agreeAdd : t.buildings.form.create}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </BuildingCreationDialogShell>
  );
}
